import { useState, useEffect, useMemo, useRef } from 'react';
import { FileText, Search, Eye, Pencil, Trash2, ClipboardCheck, AlertCircle, Download, ArrowUp, ArrowDown, ArrowUpDown, ChevronLeft, ChevronRight, FilterX, MapPinOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';
import { getUserProfile } from '../lib/auth';
import { ConfirmModal } from './ConfirmModal';
import { useNotify } from './useNotify';
import { logAudit, AUDIT_ACTIONS, AUDIT_ENTITIES } from '../lib/auditLog';
import type { ReportStatus } from '../types/inspection';

interface InspectionReport {
  id: string;
  job_site_name: string;
  month: string;
  technicians: string[];
  is_draft: boolean;
  status_id?: string;
  status?: ReportStatus;
  rejection_reason?: string;
  created_at: string;
  updated_at: string;
  // Manager/Admin-only list extras (populated server-side for those roles).
  submitter_name?: string | null;
  submitter_email?: string | null;
  controller_count?: number;
  zone_count?: number;
  doublecheck_count?: number;
  water_meter_count?: number;
  issue_count?: number;
  duration_active_minutes?: number | null;
  duration_visit_count?: number | null;
  duration_enabled?: boolean;
  /** Live-location heartbeats recorded during the inspection; 0 = none at all. */
  location_points?: number | null;
}

interface ViewInspectionsProps {
  onViewReport: (reportId: string) => void;
  onEditReport: (reportId: string) => void;
  onReviewReport?: (reportId: string) => void;
}

type SortKey = 'job_site_name' | 'month' | 'created_at' | 'status';
type SortDir = 'asc' | 'desc';
type MonthPreset = 'all' | 'this_month' | 'last_month' | 'last_90' | 'last_6_months' | 'this_year' | 'custom';

const PAGE_SIZE_OPTIONS = [15, 25, 50];

function ymKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// Resolve a month-preset (or custom from/to) to inclusive 'YYYY-MM' bounds, or
// null for "no month filter". String comparison on 'YYYY-MM' is chronological.
function monthPresetRange(preset: MonthPreset, customFrom: string, customTo: string): { from: string; to: string } | null {
  const now = new Date();
  switch (preset) {
    case 'this_month': {
      const m = ymKey(now);
      return { from: m, to: m };
    }
    case 'last_month': {
      const m = ymKey(new Date(now.getFullYear(), now.getMonth() - 1, 1));
      return { from: m, to: m };
    }
    case 'last_90': {
      const d = new Date(now);
      d.setDate(d.getDate() - 90);
      return { from: ymKey(d), to: ymKey(now) };
    }
    case 'last_6_months':
      return { from: ymKey(new Date(now.getFullYear(), now.getMonth() - 5, 1)), to: ymKey(now) };
    case 'this_year':
      return { from: `${now.getFullYear()}-01`, to: ymKey(now) };
    case 'custom':
      if (!customFrom && !customTo) return null;
      return { from: customFrom || '0000-00', to: customTo || '9999-99' };
    default:
      return null;
  }
}

// Persist the list's filters/sort/page for the session so returning from a
// report (which unmounts this view) restores what the user had set.
const FILTERS_STORAGE_KEY = 'irrig.viewInspections.filters';

interface PersistedFilters {
  searchTerm?: string;
  statusFilter?: string;
  monthPreset?: MonthPreset;
  monthFrom?: string;
  monthTo?: string;
  sortKey?: SortKey;
  sortDir?: SortDir;
  pageSize?: number;
  currentPage?: number;
}

function loadPersistedFilters(): PersistedFilters {
  try {
    const raw = sessionStorage.getItem(FILTERS_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as PersistedFilters) : {};
  } catch {
    return {};
  }
}

export function ViewInspections({ onViewReport, onEditReport, onReviewReport }: ViewInspectionsProps) {
  const { t, i18n } = useTranslation('inspection');
  const { t: tCommon } = useTranslation('common');
  const { notify, NotifyModal } = useNotify();
  const restored = useRef(loadPersistedFilters()).current;
  const isFirstRender = useRef(true);
  const [reports, setReports] = useState<InspectionReport[]>([]);
  const [filteredReports, setFilteredReports] = useState<InspectionReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(restored.searchTerm ?? '');
  const [statusFilter, setStatusFilter] = useState<string>(restored.statusFilter ?? 'all');
  const [monthPreset, setMonthPreset] = useState<MonthPreset>(restored.monthPreset ?? 'all');
  const [monthFrom, setMonthFrom] = useState<string>(restored.monthFrom ?? '');
  const [monthTo, setMonthTo] = useState<string>(restored.monthTo ?? '');
  const [statuses, setStatuses] = useState<ReportStatus[]>([]);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ reportId: string; jobSiteName: string } | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>(restored.sortKey ?? 'created_at');
  const [sortDir, setSortDir] = useState<SortDir>(restored.sortDir ?? 'desc');
  const [currentPage, setCurrentPage] = useState(restored.currentPage ?? 1);
  const [pageSize, setPageSize] = useState(
    PAGE_SIZE_OPTIONS.includes(restored.pageSize as number) ? (restored.pageSize as number) : 25,
  );

  useEffect(() => {
    fetchUserRole();
    // fetchReports() loads report-statuses too and sets them — no separate
    // fetchStatuses() call here, which was a duplicate round trip on mount.
    fetchReports();
  }, []);

  useEffect(() => {
    filterReports();
  }, [reports, searchTerm, statusFilter, monthPreset, monthFrom, monthTo]);

  // Reset to page 1 when filters/sort change — but not on the initial mount,
  // so a restored page survives returning from a report.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setCurrentPage(1);
  }, [searchTerm, statusFilter, monthPreset, monthFrom, monthTo, sortKey, sortDir]);

  // Persist filters/sort/page for the session so they restore on return.
  useEffect(() => {
    try {
      sessionStorage.setItem(
        FILTERS_STORAGE_KEY,
        JSON.stringify({ searchTerm, statusFilter, monthPreset, monthFrom, monthTo, sortKey, sortDir, pageSize, currentPage }),
      );
    } catch {
      // storage unavailable (private mode / quota) — non-fatal
    }
  }, [searchTerm, statusFilter, monthPreset, monthFrom, monthTo, sortKey, sortDir, pageSize, currentPage]);

  const fetchUserRole = async () => {
    try {
      const profile = await getUserProfile();
      setUserRole(profile?.role ?? null);
    } catch (error) {
      console.error('Error fetching user role:', error);
    }
  };

  const fetchReports = async () => {
    try {
      // Server applies §3.3 visibility rules; client receives the already-filtered list.
      // Status is joined server-side via GET /api/reports → listReports, but the bare
      // InspectionReport row doesn't include status object. Fetch statuses separately and
      // merge client-side by status_id.
      const [rawReports, allStatuses] = await Promise.all([
        apiClient.get<InspectionReport[]>('/api/reports'),
        apiClient.get<ReportStatus[]>('/api/report-statuses'),
      ]);

      const statusMap = new Map(allStatuses.map(s => [s.id, s]));
      const data = rawReports.map(r => ({
        ...r,
        status: r.status_id ? (statusMap.get(r.status_id) ?? undefined) : undefined,
      }));

      setReports(data);
      setStatuses(allStatuses);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterReports = () => {
    let filtered = [...reports];

    // Property column filter: substring match on the property name.
    if (searchTerm.trim()) {
      const q = searchTerm.trim().toLowerCase();
      filtered = filtered.filter(report => report.job_site_name.toLowerCase().includes(q));
    }

    // Status column filter.
    if (statusFilter !== 'all') {
      filtered = filtered.filter(report => report.status_id === statusFilter);
    }

    // Month column filter: preset timespan or custom range, as inclusive
    // 'YYYY-MM' bounds compared against the report month prefix.
    const range = monthPresetRange(monthPreset, monthFrom, monthTo);
    if (range) {
      filtered = filtered.filter(report => {
        const m = report.month.slice(0, 7);
        return m >= range.from && m <= range.to;
      });
    }

    setFilteredReports(filtered);
  };

  const sortedReports = useMemo(() => {
    const sorted = [...filteredReports];
    sorted.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'job_site_name':
          cmp = a.job_site_name.localeCompare(b.job_site_name);
          break;
        case 'month':
          cmp = a.month.localeCompare(b.month);
          break;
        case 'created_at':
          cmp = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
        case 'status':
          cmp = (a.status?.label || '').localeCompare(b.status?.label || '');
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [filteredReports, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sortedReports.length / pageSize));
  const paginatedReports = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedReports.slice(start, start + pageSize);
  }, [sortedReports, currentPage, pageSize]);

  // After load, clamp a restored page that's now out of range (e.g. the data
  // changed since the last visit) so the list never shows an empty page.
  useEffect(() => {
    if (!loading && currentPage > totalPages) setCurrentPage(totalPages);
  }, [loading, currentPage, totalPages]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'job_site_name' ? 'asc' : 'desc');
    }
  };

  const SortIcon = ({ columnKey }: { columnKey: SortKey }) => {
    if (sortKey !== columnKey) return <ArrowUpDown size={12} className="text-theme-secondary" />;
    return sortDir === 'asc'
      ? <ArrowUp size={12} className="text-blue-600" />
      : <ArrowDown size={12} className="text-blue-600" />;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(i18n.language, {
      month: 'short',
      day: 'numeric',
    });
  };

  const formatMonth = (monthString: string) => {
    const [year, month] = monthString.split('-');
    return new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString(i18n.language, {
      month: 'short',
      year: '2-digit',
    });
  };

  const handleDeleteReport = async () => {
    if (!deleteConfirm) return;

    try {
      await apiClient.del(`/api/reports/${deleteConfirm.reportId}`);

      logAudit({
        action: AUDIT_ACTIONS.DELETE,
        entityType: AUDIT_ENTITIES.INSPECTION_REPORT,
        entityId: deleteConfirm.reportId,
        entityLabel: deleteConfirm.jobSiteName,
        pageContext: 'view-inspections',
      });

      setDeleteConfirm(null);
      await fetchReports();
    } catch (error) {
      console.error('Error deleting report:', error);
      notify(tCommon('status.errorTitle'), t('viewInspections.errorDelete'), 'danger');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-theme-secondary">{t('viewInspections.loading')}</p>
      </div>
    );
  }

  const canEdit = (report: InspectionReport) =>
    (report.is_draft || report.status?.status_type === 'draft' || report.status?.status_type === 'rejected' || report.status?.status_type === 'in_progress') && userRole;

  const canReview = (report: InspectionReport) =>
    (userRole === 'Manager' || userRole === 'Admin') && report.status?.status_type === 'submitted' && onReviewReport;

  // Managers/Admins get four extra, compact single-line columns (submitter,
  // device counts, total issues, time-on-site) that Users never see.
  const isManager = userRole === 'Manager' || userRole === 'Admin';
  const gridCols = isManager
    ? 'minmax(160px, 1fr) 120px 84px 120px 132px 60px 128px 120px 148px'
    : 'minmax(200px, 1fr) 168px 88px 132px 152px';

  // Same "1h 45m · 2 visits" format the manager review uses.
  const formatDuration = (mins: number): string => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h > 0 && m > 0) return t('review.durationHm', { h, m, defaultValue: `${h}h ${m}m` });
    if (h > 0) return t('review.durationH', { h, defaultValue: `${h}h` });
    return t('review.durationM', { m, defaultValue: `${m}m` });
  };
  const timeOnSite = (r: InspectionReport): string | null => {
    if (r.duration_enabled === false || r.duration_active_minutes == null) return null;
    const base = formatDuration(r.duration_active_minutes);
    const visits = r.duration_visit_count ?? 0;
    return visits > 1 ? `${base} · ${t('review.visitsCount', { count: visits })}` : base;
  };
  // Read-only integrity flag: a finished inspection that recorded zero location
  // heartbeats means the tech's device never reported a position for the whole
  // job — the silent failure the passive tracker can't announce by itself (and
  // the reason a technician once went days with no trail before anyone noticed).
  // Drafts/in-progress rows are excluded: they may simply not have run yet.
  // null/undefined = we don't know (the report predates the activity telemetry,
  // or the batch failed) → show nothing rather than a false accusation.
  const missingLocation = (r: InspectionReport): boolean => {
    const inProgress =
      r.is_draft || r.status?.status_type === 'draft' || r.status?.status_type === 'in_progress';
    return !inProgress && r.location_points === 0;
  };
  const devicesTooltip = t('viewInspections.devicesTooltip', {
    defaultValue: 'Controllers · Zones · Double checks · Water meters',
  });

  const filtersActive =
    searchTerm.trim() !== '' || statusFilter !== 'all' || monthPreset !== 'all' || monthFrom !== '' || monthTo !== '';

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setMonthPreset('all');
    setMonthFrom('');
    setMonthTo('');
  };

  return (
    <div className="max-w-7xl mx-auto h-full flex flex-col overflow-hidden">
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <h1 className="text-xl sm:text-2xl font-bold text-theme-primary">{t('viewInspections.heading')}</h1>
        <div className="flex items-center gap-3">
          {filtersActive && (
            <button
              onClick={clearFilters}
              className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 transition-colors"
            >
              <FilterX size={14} /> {t('viewInspections.clearFilters')}
            </button>
          )}
          <span className="text-xs text-theme-secondary">
            {filteredReports.length} of {reports.length}
          </span>
        </div>
      </div>

      <div className="bg-surface rounded-lg shadow-sm border border-line flex flex-col min-h-0 flex-1">
          <div
            className="grid items-start px-2 sm:px-3 py-2.5 border-b border-line bg-subtle rounded-t-lg gap-x-3 flex-shrink-0"
            style={{ gridTemplateColumns: gridCols }}
          >
            {/* Property: sort + type-to-filter */}
            <div className="flex flex-col gap-1 min-w-0">
              <button
                onClick={() => handleSort('job_site_name')}
                className="flex items-center gap-1 text-[11px] font-semibold text-theme-secondary uppercase tracking-wider hover:text-theme-primary transition-colors"
              >
                {t('viewInspections.colProperty')} <SortIcon columnKey="job_site_name" />
              </button>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-theme-secondary" size={13} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t('viewInspections.filterProperty')}
                  className="w-full pl-7 pr-2 py-1 text-xs font-normal normal-case text-theme-primary bg-surface border border-line rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            {/* Month: sort + timespan preset */}
            <div className="flex flex-col gap-1">
              <button
                onClick={() => handleSort('month')}
                className="flex items-center gap-1 text-[11px] font-semibold text-theme-secondary uppercase tracking-wider hover:text-theme-primary transition-colors"
              >
                {t('viewInspections.colMonth')} <SortIcon columnKey="month" />
              </button>
              <select
                value={monthPreset}
                onChange={(e) => setMonthPreset(e.target.value as MonthPreset)}
                className="w-full px-1.5 py-1 text-xs font-normal normal-case text-theme-primary bg-surface border border-line rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">{t('viewInspections.monthAll')}</option>
                <option value="this_month">{t('viewInspections.monthThis')}</option>
                <option value="last_month">{t('viewInspections.monthLast')}</option>
                <option value="last_90">{t('viewInspections.month90')}</option>
                <option value="last_6_months">{t('viewInspections.month6')}</option>
                <option value="this_year">{t('viewInspections.monthYear')}</option>
                <option value="custom">{t('viewInspections.monthCustom')}</option>
              </select>
            </div>
            {/* Created: sort only */}
            <div className="hidden sm:flex flex-col gap-1">
              <button
                onClick={() => handleSort('created_at')}
                className="flex items-center gap-1 text-[11px] font-semibold text-theme-secondary uppercase tracking-wider hover:text-theme-primary transition-colors"
              >
                {t('viewInspections.colCreated')} <SortIcon columnKey="created_at" />
              </button>
            </div>
            {/* Manager/Admin-only columns: submitter, devices, issues, time-on-site */}
            {isManager && (
              <>
                <span className="text-[11px] font-semibold text-theme-secondary uppercase tracking-wider self-start">
                  {t('viewInspections.colSubmittedBy', { defaultValue: 'Submitted by' })}
                </span>
                <span
                  className="text-[11px] font-semibold text-theme-secondary uppercase tracking-wider self-start"
                  title={devicesTooltip}
                >
                  {t('viewInspections.colDevices', { defaultValue: 'Devices' })}
                </span>
                <span className="text-[11px] font-semibold text-theme-secondary uppercase tracking-wider self-start text-right">
                  {t('viewInspections.colIssues', { defaultValue: 'Issues' })}
                </span>
                <span className="text-[11px] font-semibold text-theme-secondary uppercase tracking-wider self-start">
                  {t('viewInspections.colTimeOnSite', { defaultValue: 'Time on site' })}
                </span>
              </>
            )}
            {/* Status: sort + filter */}
            <div className="flex flex-col gap-1">
              <button
                onClick={() => handleSort('status')}
                className="flex items-center gap-1 text-[11px] font-semibold text-theme-secondary uppercase tracking-wider hover:text-theme-primary transition-colors"
              >
                {t('viewInspections.colStatus')} <SortIcon columnKey="status" />
              </button>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-1.5 py-1 text-xs font-normal normal-case text-theme-primary bg-surface border border-line rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">{t('viewInspections.allStatus')}</option>
                {statuses.map((status) => (
                  <option key={status.id} value={status.id}>{status.label}</option>
                ))}
              </select>
            </div>
            {/* Actions */}
            <span className="text-[11px] font-semibold text-theme-secondary uppercase tracking-wider text-right pl-3">{t('viewInspections.colActions')}</span>
          </div>

          {monthPreset === 'custom' && (
            <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-line bg-subtle text-xs flex-shrink-0">
              <span className="font-medium text-theme-secondary">{t('viewInspections.dateRange')}</span>
              <input
                type="month"
                value={monthFrom}
                max={monthTo || undefined}
                onChange={(e) => setMonthFrom(e.target.value)}
                aria-label={t('viewInspections.fromMonth')}
                className="px-2 py-1 bg-surface text-theme-primary border border-line rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              />
              <span className="text-theme-secondary">–</span>
              <input
                type="month"
                value={monthTo}
                min={monthFrom || undefined}
                onChange={(e) => setMonthTo(e.target.value)}
                aria-label={t('viewInspections.toMonth')}
                className="px-2 py-1 bg-surface text-theme-primary border border-line rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => { setMonthPreset('all'); setMonthFrom(''); setMonthTo(''); }}
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                {t('viewInspections.clearDates')}
              </button>
            </div>
          )}

          <div className="overflow-y-auto min-h-0 flex-1 divide-y divide-line">
            {filteredReports.length === 0 ? (
              <div className="p-8 text-center">
                <FileText size={36} className="mx-auto text-theme-secondary mb-3" />
                <h3 className="text-sm font-semibold text-theme-primary mb-1">{t('viewInspections.noReports')}</h3>
                <p className="text-xs text-theme-secondary">
                  {reports.length === 0
                    ? t('viewInspections.noReportsHint')
                    : t('viewInspections.tryAdjusting')}
                </p>
              </div>
            ) : (
              paginatedReports.map((report) => (
              <div
                key={report.id}
                className="grid items-center px-2 sm:px-3 py-1.5 hover:bg-subtle transition-colors gap-x-3 cursor-pointer"
                style={{ gridTemplateColumns: gridCols }}
                title={report.job_site_name}
                onClick={() => onViewReport(report.id)}
              >
                <span className="font-medium text-sm text-theme-primary truncate min-w-0">
                  {report.job_site_name}
                  {report.status?.status_type === 'rejected' && report.rejection_reason && (
                    <span title={report.rejection_reason}>
                      <AlertCircle size={12} className="inline ml-1 -mt-0.5 text-red-500" />
                    </span>
                  )}
                </span>
                <span className="text-xs text-theme-secondary whitespace-nowrap">{formatMonth(report.month)}</span>
                <span className="hidden sm:block text-xs text-theme-secondary whitespace-nowrap">{formatDate(report.created_at)}</span>
                {isManager && (
                  <>
                    {/* Submitted by */}
                    <span
                      className="text-xs text-theme-secondary truncate min-w-0"
                      title={report.submitter_name || report.submitter_email || ''}
                    >
                      {report.submitter_name || report.submitter_email || '—'}
                    </span>
                    {/* Device counts (compact, single line): controllers · zones · double checks · water meters */}
                    <span
                      className="text-xs text-theme-secondary whitespace-nowrap font-mono tabular-nums"
                      title={devicesTooltip}
                    >
                      {report.controller_count ?? 0}·{report.zone_count ?? 0}·{report.doublecheck_count ?? 0}·{report.water_meter_count ?? 0}
                    </span>
                    {/* Total issues */}
                    <span className="text-xs whitespace-nowrap text-right tabular-nums">
                      {(report.issue_count ?? 0) > 0 ? (
                        <span className="font-semibold text-red-600 dark:text-red-400">{report.issue_count}</span>
                      ) : (
                        <span className="text-theme-secondary">0</span>
                      )}
                    </span>
                    {/* Time on site (+ a quiet "no location recorded" marker) */}
                    <span className="text-xs text-theme-secondary whitespace-nowrap truncate flex items-center gap-1">
                      {timeOnSite(report) ?? <span className="text-theme-secondary">—</span>}
                      {missingLocation(report) && (
                        <span
                          className="flex-shrink-0 text-amber-600 dark:text-amber-400"
                          aria-label={t('viewInspections.noLocationRecorded', {
                            defaultValue: 'No location recorded',
                          })}
                          title={t('viewInspections.noLocationTooltip', {
                            defaultValue:
                              'No location was recorded during this inspection, so it has no map trail and its photos have no map pin. Location was most likely turned off on the device.',
                          })}
                        >
                          <MapPinOff size={13} />
                        </span>
                      )}
                    </span>
                  </>
                )}
                <span className="text-left">
                  {report.status ? (
                    <span
                      className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold rounded text-white leading-none whitespace-nowrap"
                      style={{ backgroundColor: report.status.color }}
                    >
                      {report.status.label}
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-1.5 py-0.5 bg-subtle text-theme-secondary text-[10px] font-semibold rounded leading-none whitespace-nowrap">
                      {t('viewInspections.statusUnknown')}
                    </span>
                  )}
                </span>
                <div className="flex items-center gap-0 justify-end flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => onViewReport(report.id)}
                    className="p-1 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-500/10 rounded transition-colors"
                    title={t('report.viewReport')}
                  >
                    <Eye size={15} />
                  </button>
                  {canEdit(report) && (
                    <button
                      onClick={() => onEditReport(report.id)}
                      className="p-1 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-500/10 rounded transition-colors"
                      title={report.status?.status_type === 'rejected' ? t('viewInspections.editResubmit') : t('report.editReport')}
                    >
                      <Pencil size={15} />
                    </button>
                  )}
                  {canReview(report) && (
                    <button
                      onClick={() => onReviewReport!(report.id)}
                      className="p-1 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-500/10 rounded transition-colors"
                      title={t('viewInspections.reviewReport')}
                    >
                      <ClipboardCheck size={15} />
                    </button>
                  )}
                  {report.status?.status_type === 'approved' && (
                    <button
                      onClick={() => onViewReport(report.id)}
                      className="p-1 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-500/10 rounded transition-colors"
                      title={t('viewer.downloadReport')}
                    >
                      <Download size={15} />
                    </button>
                  )}
                  {userRole === 'Admin' && (
                    <button
                      onClick={() => setDeleteConfirm({ reportId: report.id, jobSiteName: report.job_site_name })}
                      className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 rounded transition-colors"
                      title={t('report.deleteReport')}
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              </div>
              ))
            )}
          </div>

          {filteredReports.length > 0 && (
          <div className="flex items-center justify-between px-3 py-2 border-t border-line bg-subtle rounded-b-lg flex-shrink-0">
            <div className="flex items-center gap-2">
              <span className="text-xs text-theme-secondary">{t('viewInspections.rowsPerPage')}</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="text-xs bg-surface text-theme-primary border border-line rounded px-1.5 py-0.5 focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-3">
              <span className="text-xs text-theme-secondary">
                {t('viewInspections.pagination', {
                  from: (currentPage - 1) * pageSize + 1,
                  to: Math.min(currentPage * pageSize, sortedReports.length),
                  total: sortedReports.length,
                })}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage <= 1}
                  className="p-1 rounded hover:bg-subtle disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft size={16} className="text-theme-secondary" />
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage >= totalPages}
                  className="p-1 rounded hover:bg-subtle disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight size={16} className="text-theme-secondary" />
                </button>
              </div>
            </div>
          </div>
          )}
        </div>

      <ConfirmModal
        isOpen={deleteConfirm !== null}
        title={t('viewInspections.deleteTitle')}
        message={t('viewInspections.deleteMessage', { name: deleteConfirm?.jobSiteName ?? '' })}
        confirmText={tCommon('actions.delete')}
        cancelText={tCommon('actions.cancel')}
        variant="danger"
        onConfirm={handleDeleteReport}
        onCancel={() => setDeleteConfirm(null)}
      />

      {NotifyModal}
    </div>
  );
}
